/** Initial BAC locality policy: consecutive ~15-mile bands, never a radius. */
export const BAC_LOCALITY_BAND_METERS = 24_140;
export type GeoAnchor = { lat: number; lng: number };

export function geoAnchor(lat: unknown, lng: unknown): GeoAnchor | null {
  return typeof lat === "number" && typeof lng === "number" &&
    Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180
    ? { lat, lng } : null;
}

export function localityBand(distanceMeters: number): number {
  return Number.isFinite(distanceMeters) && distanceMeters >= 0
    ? Math.floor(distanceMeters / BAC_LOCALITY_BAND_METERS) : Number.MAX_SAFE_INTEGER;
}

export function distanceMeters(a: GeoAnchor, b: GeoAnchor): number {
  const rad = Math.PI / 180;
  const h = Math.sin((b.lat - a.lat) * rad / 2) ** 2 +
    Math.cos(a.lat * rad) * Math.cos(b.lat * rad) * Math.sin((b.lng - a.lng) * rad / 2) ** 2;
  return 6_371_000 * 2 * Math.asin(Math.sqrt(Math.min(1, Math.max(0, h))));
}

export function mapAnchor(north: number, west: number, south: number, east: number): GeoAnchor | null {
  if (!geoAnchor(north, west) || !geoAnchor(south, east) || north < south) return null;
  const span = east >= west ? east - west : east + 360 - west;
  const lng = ((west + span / 2 + 540) % 360) - 180;
  return { lat: (north + south) / 2, lng };
}

/**
 * Provider geo grouping happens BEFORE custom ranking and pagination.
 * Bounding-box searches must retain their exact rectangle: Algolia otherwise
 * ignores aroundLatLng when insideBoundingBox is present. Use private numeric
 * copies of the already-indexed public coordinates to preserve that filter.
 * Unsupported geometries keep the canonical path, never a widened substitute.
 */
export function bacLocalityParams(params: Record<string, unknown>): Record<string, unknown> | null {
  if (params.insidePolygon || params.aroundLatLngViaIP) return null;
  const box = params.insideBoundingBox;
  if (box != null) {
    let values: unknown = box;
    if (typeof values === "string") values = values.split(",").map(Number);
    if (Array.isArray(values) && values.length === 1 && Array.isArray(values[0])) values = values[0];
    if (!Array.isArray(values) || values.length !== 4 || !values.every(v => typeof v === "number")) return null;
    const [north, west, south, east] = values as number[];
    const anchor = mapAnchor(north!, west!, south!, east!);
    if (!anchor) return null;
    const existing = params.numericFilters == null ? [] :
      Array.isArray(params.numericFilters) ? params.numericFilters : [params.numericFilters];
    const { insideBoundingBox: _box, ...rest } = params;
    return { ...rest, aroundLatLng: `${anchor.lat},${anchor.lng}`, aroundRadius: "all",
      aroundPrecision: BAC_LOCALITY_BAND_METERS,
      numericFilters: [...existing,
        `bac_geo_latitude>=${south}`, `bac_geo_latitude<=${north}`,
        ...(west! <= east! ? [`bac_geo_longitude>=${west}`, `bac_geo_longitude<=${east}`] :
          [[`bac_geo_longitude>=${west}`, `bac_geo_longitude<=${east}`]])] };
  }
  if (typeof params.aroundLatLng !== "string") return null;
  const coordinates = params.aroundLatLng.split(",").map(v => v.trim() === "" ? NaN : Number(v));
  if (coordinates.length !== 2 || !geoAnchor(coordinates[0], coordinates[1])) return null;
  // Keep the caller's radius, filters, page and facets unchanged.
  return { ...params, aroundPrecision: BAC_LOCALITY_BAND_METERS };
}
